import PropTypes from 'prop-types'
import {
  Children,
  cloneElement,
  isValidElement,
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef
} from 'react'
import classNames from 'lib/util/classNames'
import debounce from 'lib/util/debounce'
import { useSlideshowContext } from '../Slideshow'
import styles from './Slides.module.scss'

/**
 * The `Slides`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function Slides (props) {
  const { border, children, className, useShadow } = props
  const ref = useRef()
  const {
    hasOverflowLeft,
    hasOverflowRight,
    setGroupCount,
    setHasOverflowLeft,
    setHasOverflowRight,
    setIsInteracting,
    setNumSlides,
    setSlideIndex
  } = useSlideshowContext()
  const classNameOutput = useMemo(
    () =>
      classNames([
        className,
        styles.container,
        hasOverflowLeft && styles.overflowLeft,
        hasOverflowRight && styles.overflowRight,
        useShadow && styles.useShadow
      ]),
    [className, hasOverflowLeft, hasOverflowRight, useShadow]
  )
  const classNameBorder = useMemo(
    () => classNames([className, styles.border]),
    [className]
  )
  // Map over children and clone them with an index prop
  const slidesWithIndex = Children.toArray(children)
    .filter(child => isValidElement(child))
    .map((validChild, index) => cloneElement(validChild, { index }))

  /**
   * Handle mouse move
   */
  const handleMouseMove = useCallback(e => {
    const target = e.target

    if (
      target.tagName === 'A' ||
      target.tagName === 'BUTTON' ||
      target.closest('a') ||
      target.closest('button')
    ) {
      setIsInteracting(true)
    } else {
      setIsInteracting(false)
    }
  }, [])

  /**
   * Handle mouse leave
   */
  const handleMouseLeave = useCallback(() => {
    setIsInteracting(false)
  }, [])

  /**
   * Effect: On updates
   */
  useLayoutEffect(() => {
    const el = ref.current
    const computed = window.getComputedStyle(el)
    const columnGap = parseInt(computed.columnGap, 10) || 0

    // Calculate slide width based on the container width
    let itemWidth =
      el?.firstElementChild?.clientWidth || el.clientWidth + columnGap

    // Store the last known scroll position
    let lastSlideIndex = 0

    const handleScroll = () => {
      const scrollPosition = el.scrollLeft
      const currentSlideIndex = Math.floor(
        (scrollPosition + itemWidth * 0.5) / itemWidth
      )

      if (currentSlideIndex !== lastSlideIndex) {
        setSlideIndex(currentSlideIndex)

        lastSlideIndex = currentSlideIndex
      }

      const isStart = !el.scrollLeft
      const isEnd = el.scrollLeft + el.clientWidth + 1 >= el.scrollWidth

      setHasOverflowLeft(!isStart)
      setHasOverflowRight(!isEnd)
    }

    const handleResize = () => {
      const target = el?.firstElementChild || el
      const containerWidth = el.clientWidth
      const itemClientWidth = target.clientWidth
      const columnGap = parseInt(computed.columnGap, 10) || 0

      itemWidth = itemClientWidth + columnGap

      const groupCount = Math.floor((containerWidth + columnGap) / itemWidth)

      setGroupCount(groupCount)
      handleScroll()
    }

    el.addEventListener('scroll', handleScroll)

    const debouncedHandleResize = debounce(handleResize, 0)
    const observer = new ResizeObserver(debouncedHandleResize)

    observer.observe(el)
    handleResize()

    setNumSlides(slidesWithIndex.length)

    return () => {
      el.removeEventListener('scroll', handleScroll)
      debouncedHandleResize.cancel()
      observer.disconnect()
    }
  }, [slidesWithIndex, setNumSlides, setSlideIndex])

  return (
    <>
      <div
        ref={ref}
        className={classNameOutput}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {slidesWithIndex}
      </div>
      {border && <div className={classNameBorder} />}
    </>
  )
}

/** @type {object} */
Slides.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  border: PropTypes.bool,
  useShadow: PropTypes.bool
}

/** @type {object} */
Slides.defaultProps = {
  className: '',
  children: null,
  border: false,
  useShadow: false
}

// Memoize
export default memo(Slides)
